@use '../../styles/box-sizing';

$month-title-line-height: calc(var(--der-ui-spacing-5XS) + var(--der-ui-icon-size-M)); // [1]
$month-control-top-offset: calc(($month-title-line-height)/2 + var(--der-ui-spacing-3XS)); // [2]
$months-column-breakpoint: 768px;
$day-selected-range-background: content-box var(--der-ui-color-background-call-to-action-primary-default, #ffc356);
$day-selected-range-end-background: content-box linear-gradient(to right, var(--der-ui-color-background-call-to-action-primary-default, #ffc356) 0%, var(--der-ui-color-background-call-to-action-primary-default, #ffc356) 50%, transparent 50%, transparent 100%);
$day-selected-range-start-background: content-box linear-gradient(to right, transparent 0%, transparent 50%, var(--der-ui-color-background-call-to-action-primary-default, #ffc356) 50%, var(--der-ui-color-background-call-to-action-primary-default, #ffc356) 100%);

/* [1]
  This calculation is used to position the month control buttons vertically centered relative to the month title.
  It combines the spacing and icon size to ensure proper alignment.
*/

/* [2]
  This calculation is used to position the month control buttons at the top of the calendar.
  It takes into account the line height and top-padding of the month title.
*/

.c-calendar {
  padding: var(--der-ui-spacing-S);
  background-color: var(--der-ui-color-background-general-default);
  container-type: inline-size;
  container-name: calendar;
  position: relative;
}

.c-calendar__month-control {
  position: absolute;
  z-index: 10;
  top: $month-control-top-offset;
}

.c-calendar__month-control--prev {
  left: var(--der-ui-spacing-S);
}

.c-calendar__month-control--next {
  right: var(--der-ui-spacing-S);
}

.c-calendar__month-title-container {
  margin-bottom: var(--der-ui-spacing-XS);
  position: relative;
  display: flex;
  justify-content: space-around;
}

.c-calendar__month-title {
  padding: var(--der-ui-spacing-3XS);
  font-size: var(--der-ui-font-size-component-title);
  font-family: var(--der-ui-font-face-component-title);
  font-style: var(--der-ui-font-style-component-title);
  font-weight: var(--der-ui-font-weight-component-title);
  line-height: var(--der-ui-line-height-component-title);
  text-transform: var(--der-ui-text-transform-component-title);
  color: var(--der-ui-color-foreground-heading-component-title);
  position: relative;
  line-height: $month-title-line-height;
}

.c-calendar__month-selector {
  background-color: var(--der-ui-color-background-general-default);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: none;
  outline: none;

  option {
    text-transform: var(--der-ui-text-transform-general-label);
    background-color: var(--der-ui-color-background-general-default);
  }
}

.c-calendar__months {
  display: flex;
  gap: var(--der-ui-spacing-M);

  @container calendar (max-width: #{$months-column-breakpoint}) {
    flex-direction: column;
  }
}

.c-calendar__month {
  flex: 1;
}

.c-calendar__weekdays {
  margin-bottom: var(--der-ui-spacing-3XS);
  text-transform: var(--der-ui-text-transform-general-body-reduced);
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--der-ui-spacing-5XS);
  text-align: center;
  cursor: default;
}

.c-calendar__weekday {
  color: var(--der-ui-color-foreground-general-mute);
  padding: var(--der-ui-spacing-XS) var(--der-ui-spacing-5XS);
}

.c-calendar__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.c-calendar__day {
  text-transform: var(--der-ui-text-transform-general-label-bold);
  color: var(--der-ui-color-foreground-general-default);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.c-calendar__day--unselectable {
  color: var(--der-ui-color-foreground-general-disabled);
  cursor: default;
}

.c-calendar__day--selected-range {
  color: var(--der-ui-color-foreground-call-to-action-primary-default);
  background: $day-selected-range-background;
}

.c-calendar__day--selected-start {
  background: $day-selected-range-start-background;
}

.c-calendar__day--selected-end {
  background: $day-selected-range-end-background;
}

.c-calendar__day--today {
  padding: var(--der-ui-spacing-3XS);
  color: var(--der-ui-color-foreground-general-default);
}

.c-calendar__day-number {
  border-radius: var(--der-ui-radius-round);
  height: var(--der-ui-height-l);
  display: flex;
  width: var(--der-ui-height-l);
  justify-content: center;
  align-items: center;
  font-size: var(--der-ui-font-size-general-label-bold);
  font-family: var(--der-ui-font-face-general-label-bold);
  font-style: var(--der-ui-font-style-general-label-bold);
  font-weight: var(--der-ui-font-weight-general-label-bold);
  line-height: var(--der-ui-line-height-general-label-bold);
  text-transform: var(--der-ui-text-transform-general-label-bold);

  // Apply hover background color to the day number only if the day is selectable and not selected
  .c-calendar__day:not(.c-calendar__day--unselectable):not(.c-calendar__day--selected):hover & {
    background-color: var(--der-ui-color-background-general-mute, rgba(33, 33, 33, 0.05));
  }

  &:focus-visible {
    outline: 2px solid var(--der-ui-color-border-general-focus-outer);
    outline-offset: 2px;
  }

  .c-calendar__day--selected & {
    background-color: var(--der-ui-color-background-general-alternate);
    color: var(--der-ui-color-foreground-general-default-inverted);
    border: var(--der-ui-border-width-focus, 2px) solid var(--der-ui-color-border-general-default, #212121);
  }

  .c-calendar__day--today & {
    border: var(--der-ui-border-width-focus, 2px) solid var(--der-ui-color-border-general-default, #212121);
  }
  
}
